import { REPORT_CATEGORY_OPTIONS } from '@/app/dashboards/utils/reports.enum';
import {
  BASE_BREAKDOWN_OPTIONS,
  REPORT_PREVIEW_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';

// Types
export interface BreakdownConfig {
  secondary_breakdown_options: string[];
}

export interface BreakdownOptions {
  [key: string]: BreakdownConfig;
}

export interface AggregationOption {
  value: string;
  label: string;
  text?: string;
  icon?: string;
  formattedValue?: string;
  hideByVisualizationTypes?: string[];
  [key: string]: unknown;
}

export enum VISUALIZATION_OPTIONS {
  DOUGHNUT_CHART = 'DOUGHNUT_CHART',
  GRAPH = 'GRAPH',
  HORIZONTAL_BAR_CHART = 'HORIZONTAL_BAR_CHART',
  LINE_CHART = 'LINE_CHART',
  SINGLE_BAR_GRAPH = 'SINGLE_BAR_GRAPH',
  STACKED_BAR_GRAPH = 'STACKED_BAR_GRAPH',
  TABLE = 'TABLE',
  TOTAL = 'TOTAL',
  VERTICAL_BAR_CHART = 'VERTICAL_BAR_CHART',
}

export const VISUALIZATION_OPTION_DISPLAY_NAMES: Record<string, string> = Object.freeze({
  DOUGHNUT_CHART: 'Doughnut Chart',
  GRAPH: 'Graph',
  HORIZONTAL_BAR_CHART: 'Horizontal Bar Chart',
  LINE_CHART: 'Line Chart',
  SINGLE_BAR_GRAPH: 'Single Bar Chart',
  STACKED_BAR_GRAPH: 'Stacked Bar Chart',
  TABLE: 'Table',
  TOTAL: 'Aggregate Total Report',
  VERTICAL_BAR_CHART: 'Vertical Bar Chart',
});

// Map backend aggregation keys to enum objects for different report categories
const PRIMARY_AGGREGATION_MAP: Record<string, AggregationOption> = Object.freeze({
  TOTAL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOTAL,
  AGE_GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE_GENDER,
  AGE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.AGE,
  ASSIGNEE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.ASSIGNEE,
  BOARD: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BOARD,
  BRAND: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND,
  BRAND_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.BRAND_TAG,
  CAMPAIGN: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CAMPAIGN,
  CHANNEL: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CHANNEL,
  CITY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CITY,
  COMPETITOR_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.COMPETITOR_TAG,
  CONTENT_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CONTENT_TAG,
  COUNTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.COUNTRY,
  CREATOR_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.CREATOR_TAG,
  GALLERY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.GALLERY,
  GENDER: REPORT_PREVIEW_AGGREGATE_BY_TYPES.GENDER,
  INDUSTRY: REPORT_PREVIEW_AGGREGATE_BY_TYPES.INDUSTRY,
  MEDIA_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  MENTION_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MENTION_TYPE,
  MESSAGE_TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TAG,
  MESSAGE_TYPE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.MESSAGE_TYPE,
  PUBLISHER_PLATFORM: REPORT_PREVIEW_AGGREGATE_BY_TYPES.PUBLISHER_PLATFORM,
  REGION: REPORT_PREVIEW_AGGREGATE_BY_TYPES.REGION,
  SENTIMENT: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SENTIMENT,
  SOURCE_CAMPAIGN: REPORT_PREVIEW_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN,
  STATE_PROVINCE: REPORT_PREVIEW_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  TAG: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TAG,
  TOPIC: REPORT_PREVIEW_AGGREGATE_BY_TYPES.TOPIC,
  FOLLOWER_STATUS: REPORT_PREVIEW_AGGREGATE_BY_TYPES.FOLLOWER_STATUS,
});

// Map backend secondary breakdown keys to enum objects
const SECONDARY_BREAKDOWN_MAP: Record<string, AggregationOption> = Object.freeze({
  NONE: BASE_BREAKDOWN_OPTIONS.NONE,
  AGE: BASE_BREAKDOWN_OPTIONS.AGE,
  ASSIGNEE: BASE_BREAKDOWN_OPTIONS.ASSIGNEE,
  BOARD: BASE_BREAKDOWN_OPTIONS.BOARD,
  BRAND: BASE_BREAKDOWN_OPTIONS.BRAND,
  BRAND_TAG: BASE_BREAKDOWN_OPTIONS.BRAND_TAG,
  CAMPAIGN: BASE_BREAKDOWN_OPTIONS.CAMPAIGN,
  CHANNEL: BASE_BREAKDOWN_OPTIONS.CHANNEL,
  CITY: BASE_BREAKDOWN_OPTIONS.CITY,
  COMPETITOR_TAG: BASE_BREAKDOWN_OPTIONS.COMPETITOR_TAG,
  CONTENT_TAG: BASE_BREAKDOWN_OPTIONS.CONTENT_TAG,
  COUNTRY: BASE_BREAKDOWN_OPTIONS.COUNTRY,
  CREATOR_TAG: BASE_BREAKDOWN_OPTIONS.CREATOR_TAG,
  GALLERY: BASE_BREAKDOWN_OPTIONS.GALLERY,
  GENDER: BASE_BREAKDOWN_OPTIONS.GENDER,
  INDUSTRY: BASE_BREAKDOWN_OPTIONS.INDUSTRY,
  MEDIA_TYPE: BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE,
  MENTION_TYPE: BASE_BREAKDOWN_OPTIONS.MENTION_TYPE,
  MESSAGE_TYPE: BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE,
  SENTIMENT: BASE_BREAKDOWN_OPTIONS.SENTIMENT,
  STATE_PROVINCE: BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE,
  TAG: BASE_BREAKDOWN_OPTIONS.TAG,
  TOPIC: BASE_BREAKDOWN_OPTIONS.TOPIC,
  FOLLOWER_STATUS: BASE_BREAKDOWN_OPTIONS.FOLLOWER_STATUS,
});

// Remove when removing newStatsEngineEnabled/canAccessStatsEngineV2 flag
export const legacyCategoryByVisualizationType: Record<string, VISUALIZATION_OPTIONS[]> = {
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
    VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH,
  ],
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    VISUALIZATION_OPTIONS.HORIZONTAL_BAR_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    VISUALIZATION_OPTIONS.DOUGHNUT_CHART,
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.EARNED.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
    VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH,
  ],
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id]: [
    VISUALIZATION_OPTIONS.GRAPH,
    VISUALIZATION_OPTIONS.LINE_CHART,
    VISUALIZATION_OPTIONS.TABLE,
    VISUALIZATION_OPTIONS.TOTAL,
    VISUALIZATION_OPTIONS.SINGLE_BAR_GRAPH,
  ],
};

// Default visualization set shared by most categories. Stacked is omitted
// because StackedBarBreakdown + secondary_aggregate_by handles it.
const DEFAULT_CATEGORY_VISUALIZATIONS: VISUALIZATION_OPTIONS[] = [
  VISUALIZATION_OPTIONS.LINE_CHART,
  VISUALIZATION_OPTIONS.VERTICAL_BAR_CHART,
  VISUALIZATION_OPTIONS.TABLE,
  VISUALIZATION_OPTIONS.TOTAL,
];

export const categoryByVisualizationType: Record<string, VISUALIZATION_OPTIONS[]> = {
  [REPORT_CATEGORY_OPTIONS.OWNED.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.EARNED.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id]: DEFAULT_CATEGORY_VISUALIZATIONS,
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: [
    ...DEFAULT_CATEGORY_VISUALIZATIONS,
    VISUALIZATION_OPTIONS.HORIZONTAL_BAR_CHART,
  ],
  [REPORT_CATEGORY_OPTIONS.TSI.id]: [
    ...DEFAULT_CATEGORY_VISUALIZATIONS,
    VISUALIZATION_OPTIONS.DOUGHNUT_CHART,
  ],
};

const CATEGORIES_WITHOUT_GRAPH_LINE_STACKED_BREAKDOWNS: string[] = [
  REPORT_CATEGORY_OPTIONS.OWNED.id,
  REPORT_CATEGORY_OPTIONS.COMMUNITY.id,
  REPORT_CATEGORY_OPTIONS.TSI.id,
  REPORT_CATEGORY_OPTIONS.EARNED.id,
  REPORT_CATEGORY_OPTIONS.COMPETITIVE.id,
];

/**
 * Get list of available primary aggregation keys from backend breakdown options
 */
export function getAvailablePrimaryAggregations(
  breakdownOptions: BreakdownOptions | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object') {
    return [];
  }

  return Object.keys(PRIMARY_AGGREGATION_MAP).filter((key) =>
    Object.keys(breakdownOptions).includes(key),
  );
}

/**
 * Build primary aggregation options from backend breakdown options
 * Maps backend keys to full enum objects with labels, icons, etc.
 */
export function buildPrimaryAggregationOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
): AggregationOption[] {
  const primaryKeys = getAvailablePrimaryAggregations(breakdownOptions);

  if (primaryKeys.length === 0) {
    return [];
  }

  const mappedOptions = primaryKeys
    .map((key) => {
      const enumObject = PRIMARY_AGGREGATION_MAP[key];
      return enumObject;
    })
    .filter(Boolean); // Remove undefined values

  if (mappedOptions.length === 0) {
    return [];
  }

  return mappedOptions;
}

interface BuildNestedOptions {
  enableLineChartBreakdown?: boolean;
}

// metrics objects and breakdown options from backend
export function buildNestedVisualizationOptionsByCategory(
  breakdownOptions: BreakdownOptions | null | undefined,
  categoryVisualizationMap: Record<string, VISUALIZATION_OPTIONS[]> = categoryByVisualizationType,
  options: BuildNestedOptions = {},
) {
  /*
  return format
    {
      community_report: {
        TOTAL: {
          options: null,
        },
        LINE_CHART: {
          options: null,
        },
        TABLE: {
          options: [
            { value: 'BRAND', label: 'by Brand', formattedValue: 'Brand' },
            { value: 'CHANNEL', label: 'by Channel', formattedValue: 'Channel' },
            ...
          ]
        },
        VERTICAL_BAR_CHART: {
          options: [...]
        }
      },
      tsi_report: {
        TOTAL: { options: null },
        LINE_CHART: { options: null },
        ...
      },
      ...
    }
  */

  if (!breakdownOptions) {
    return {};
  }

  const result: Record<string, Record<string, { options: AggregationOption[] | null }>> = {};

  // Get primary aggregation options from backend
  const primaryAggregations = buildPrimaryAggregationOptions(breakdownOptions);

  // Build for each report category
  Object.entries(categoryVisualizationMap).forEach(([categoryId, visualizationTypes]) => {
    result[categoryId] = {};

    // Build for each visualization option within this category
    visualizationTypes.forEach((visualizationType) => {
      // TOTAL and LINE_CHART (in categories without graph and line chart options) don't have breakdown options (options: null)
      const isLineOrGraph =
        visualizationType === VISUALIZATION_OPTIONS.LINE_CHART ||
        visualizationType === VISUALIZATION_OPTIONS.GRAPH;
      if (
        visualizationType === VISUALIZATION_OPTIONS.TOTAL ||
        (CATEGORIES_WITHOUT_GRAPH_LINE_STACKED_BREAKDOWNS.includes(categoryId) &&
          ((isLineOrGraph && !options.enableLineChartBreakdown) ||
            visualizationType === VISUALIZATION_OPTIONS.STACKED_BAR_GRAPH))
      ) {
        result[categoryId][visualizationType] = {
          options: null,
        };
      } else {
        // Filter out primary aggregations that are not available for the visualization type
        // For example, AGE and GENDER are not available for LINE_CHART and GRAPH visualization types
        const filteredPrimaryAggregations = primaryAggregations.filter(
          (aggregation) => !aggregation.hideByVisualizationTypes?.includes(visualizationType),
        );
        result[categoryId][visualizationType] = {
          options: filteredPrimaryAggregations,
        };
      }
    });
  });

  return result;
}

/**
 * Get secondary breakdown options for a specific primary aggregation
 */
export function getSecondaryBreakdownOptions(
  breakdownOptions: BreakdownOptions | null | undefined,
  primaryAggregation: string | null | undefined,
): string[] {
  if (!breakdownOptions || typeof breakdownOptions !== 'object' || !primaryAggregation) {
    return [];
  }

  const primaryConfig = breakdownOptions[primaryAggregation];

  if (!primaryConfig?.secondary_breakdown_options) {
    return [];
  }

  return primaryConfig.secondary_breakdown_options;
}

// Maps each flag-gated dimension to the LaunchDarkly flag key that enables it.
// Adding a new gated dimension = one entry here.
export const BREAKDOWN_FEATURE_FLAGS: Record<string, string> = {
  CONTENT_TAG: 'newStatsEngineEnabled',
  MEDIA_TYPE: 'newStatsEngineEnabled',
  FOLLOWER_STATUS: 'newStatsEngineEnabled',
};

export const FEATURE_FLAGGED_DIMENSIONS = new Set(Object.keys(BREAKDOWN_FEATURE_FLAGS));

type UserFlags = Record<string, boolean | undefined>;

function hasFlagForDimension(dimension: string, userFlags: UserFlags): boolean {
  if (!FEATURE_FLAGGED_DIMENSIONS.has(dimension)) return true;
  const flagName = BREAKDOWN_FEATURE_FLAGS[dimension];
  return Boolean(userFlags[flagName]);
}

/**
 * Removes `breakdown_options` entries whose dimension is gated by a feature
 * flag the user doesn't have enabled.
 */
export function filterBreakdownOptionsByFlags(
  options: BreakdownOptions | undefined,
  userFlags: UserFlags,
): BreakdownOptions | undefined {
  if (!options) return options;
  return Object.fromEntries(
    Object.entries(options).filter(([dimension]) => hasFlagForDimension(dimension, userFlags)),
  );
}

/**
 * Build secondary breakdown options from backend breakdown options
 * Maps backend secondary keys to full enum objects.
 * Filters out dimensions whose feature flag the user doesn't have enabled.
 * Pass `{}` (or omit) to exclude all flag-gated dimensions.
 */
export function buildSecondaryBreakdownOptions(
  secondaryBreakdownOptions: string[] | null | undefined,
  visualizationType: VISUALIZATION_OPTIONS,
  userFlags: UserFlags = {},
): AggregationOption[] {
  if (!secondaryBreakdownOptions) {
    return [];
  }

  // If only NONE is available, return just NONE
  if (secondaryBreakdownOptions.length === 1 && secondaryBreakdownOptions[0] === 'NONE') {
    return [];
  }

  // No secondary breakdown options for GRAPH and LINE_CHART
  if (
    visualizationType === VISUALIZATION_OPTIONS.GRAPH ||
    visualizationType === VISUALIZATION_OPTIONS.LINE_CHART
  ) {
    return [];
  }
  return (
    secondaryBreakdownOptions
      .map((option) => SECONDARY_BREAKDOWN_MAP[option])
      // removes the option if it is hidden by the visualization type. e.g. LINE_CHART and GRAPH
      .filter((option) => option && !option.hideByVisualizationTypes?.includes(visualizationType))
      // removes the option if it requires a feature flag the user does not have
      .filter((option) => hasFlagForDimension(option.value, userFlags))
  );
}
